import React, { useState, useEffect, useRef } from 'react';
import { useCoreLayout, useAuthLayout } from '@contexts/layout';
import { useAuth } from '@contexts/auth/AuthContext';
import logo from '@assets/navbar/navbar-header.svg';
import Navbar from './Navbar';
import styles from './Layout.module.css';
import Header from './Header';
import Footer from './Footer';

type ShellRef = React.RefObject<HTMLElement>;

// An animation cancelled by an unmount or a class change rejects `finished`, and still counts as done.
// No running animation (reduced motion, jsdom) resolves at once.
const whenAnimationsFinish = (refs: ShellRef[]) =>
  Promise.allSettled(
    refs.flatMap((ref) => ref.current?.getAnimations?.() ?? []).map((a) => a.finished),
  );

/**
 * Keeps a shell element mounted while it slides out, and unmounts it when its exit animation ends.
 * `leaving` is derived during render, so the render that hides the element is the one that starts
 * its exit; an effect-driven flag would unmount it for a render and replay the exit on remount.
 */
const useExitPresence = (visible: boolean, ref: ShellRef) => {
  const [mounted, setMounted] = useState(visible);
  if (visible && !mounted) setMounted(true);
  const leaving = mounted && !visible;

  useEffect(() => {
    if (!leaving) return;
    let cancelled = false;
    void whenAnimationsFinish([ref]).then(() => {
      if (!cancelled) setMounted(false);
    });
    return () => {
      cancelled = true;
    };
  }, [leaving, ref]);

  return { mounted, leaving };
};

interface LayoutProps {
  children: React.ReactNode;
  title: string;
  onAnimationComplete?: () => void;
}

const Layout: React.FC<LayoutProps> = ({ children, title, onAnimationComplete }) => {
  const { isAuthenticated } = useAuth();

  // Core layout features - always available
  const {
    backgroundColor,
    isLogoVisible,
    isGradientVisible,
    gradientDirection,
    isLogoInitialLoad,
  } = useCoreLayout();

  // Auth layout features - only when authenticated
  const authLayout = useAuthLayout();

  // Extract auth layout values with fallbacks
  const isNavbarVisible = authLayout?.isNavbarVisible ?? false;
  const isHeaderVisible = authLayout?.isHeaderVisible ?? false;
  const isExiting = authLayout?.isExiting ?? false;
  const animationCompleted = authLayout?.animationCompleted ?? true;
  const shouldApplyPadding = authLayout?.shouldApplyPadding ?? false;
  const markShellSettled = authLayout?.markShellSettled;
  const completeExit = authLayout?.completeExit;

  const navbarRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLElement>(null);

  const navbar = useExitPresence(isNavbarVisible, navbarRef);
  const header = useExitPresence(isHeaderVisible, headerRef);

  // A route change hides the header and renames it in the same render; the leaving header keeps the
  // title it was showing rather than swapping it mid-slide.
  const [headerTitle, setHeaderTitle] = useState(title);
  if (isHeaderVisible && headerTitle !== title) setHeaderTitle(title);

  const [prevGradientVisible, setPrevGradientVisible] = useState(isGradientVisible);
  const [gradientFadingOut, setGradientFadingOut] = useState(false);

  // Handle gradient visibility changes with animation
  useEffect(() => {
    if (prevGradientVisible !== isGradientVisible) {
      if (prevGradientVisible && !isGradientVisible) {
        // Gradient is hiding - start fade out animation
        setGradientFadingOut(true);
        setTimeout(() => {
          setGradientFadingOut(false);
        }, 600); // Match fade out animation duration
      }
    }
    setPrevGradientVisible(isGradientVisible);
  }, [isGradientVisible, prevGradientVisible]);

  const exitFinished = isExiting && !navbar.leaving && !header.leaving;
  useEffect(() => {
    if (exitFinished) completeExit?.();
  }, [exitFinished, completeExit]);

  // Settled is read from the running nav, header and main animations themselves, so a change to
  // their CSS timing can't drift out of step with the page waiting on it.
  useEffect(() => {
    if (!markShellSettled || navbar.leaving || header.leaving) return;

    let cancelled = false;
    void whenAnimationsFinish([navbarRef, headerRef, mainRef]).then(() => {
      if (!cancelled) markShellSettled({ navbar: isNavbarVisible, header: isHeaderVisible });
    });
    return () => {
      cancelled = true;
    };
  }, [markShellSettled, navbar.leaving, header.leaving, isNavbarVisible, isHeaderVisible]);

  // Trigger onAnimationComplete callback when appropriate
  useEffect(() => {
    if (animationCompleted && onAnimationComplete) {
      onAnimationComplete();
    }
  }, [animationCompleted, onAnimationComplete]);

  return (
    <div className={styles.layoutContainer} style={backgroundColor ? { backgroundColor } : {}}>
      {/* Absolutely positioned logo - conditionally rendered */}
      {isLogoVisible && (
        <div
          className={`${styles.logoContainer} ${isLogoInitialLoad ? styles.logoInitialLoad : styles.logoVisible}`}
        >
          <img src={logo} alt="Preservoir Logo" className={styles.logo} />
        </div>
      )}

      {/* Radial gradient overlay - always rendered, visibility controlled by CSS */}
      <div
        className={`${styles.gradientOverlay} ${styles[`gradient-${gradientDirection}`]} ${
          isGradientVisible ? styles.fadeIn : gradientFadingOut ? styles.fadeOut : ''
        } ${isExiting ? styles.fadeOutRight : ''}`}
      ></div>

      {/* Secondary radial gradient overlay - always rendered, visibility controlled by CSS */}
      <div
        className={`${styles.secondaryGradientOverlay} ${styles[`secondary-gradient-${gradientDirection}`]} ${
          isGradientVisible ? styles.fadeIn : gradientFadingOut ? styles.fadeOut : ''
        } ${isExiting ? styles.fadeOutRight : ''}`}
      ></div>

      {/* Navbar - conditionally rendered with animation */}
      {navbar.mounted && (
        <div
          ref={navbarRef}
          className={`${styles.navbarContainer} ${navbar.leaving ? styles.slideOutLeft : ''}`}
        >
          <Navbar />
        </div>
      )}

      <div className={styles.contentWrapper}>
        {/* Header - conditionally rendered with animation */}
        {header.mounted && (
          <div
            ref={headerRef}
            className={`${styles.headerContainer} ${header.leaving ? styles.slideOutTop : ''}`}
          >
            <Header title={headerTitle} onTriggerExit={authLayout?.triggerExit} />
          </div>
        )}
        <main
          ref={mainRef}
          className={`${styles.mainContent} ${shouldApplyPadding ? styles.withPadding : ''} ${shouldApplyPadding ? styles.withMaxWidth : ''} ${isExiting ? styles.scaleOutContent : animationCompleted === false ? styles.contentDelayedEntrance : ''}`}
        >
          {/* Always show content except during exit animations */}
          {isExiting ? <div style={{ opacity: 0 }} /> : children}
        </main>
        {/* Footer - only visible when authenticated and navbar is visible */}
        {isAuthenticated && isNavbarVisible && (
          <div className={styles.footerContainer}>
            <div
              className={`${styles.footerSlide} ${isExiting ? styles.slideOutBottom : animationCompleted ? styles.slideInFromBottom : ''}`}
            >
              <Footer />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Layout;
